/**
 * 文言辞書。docs/09-design-system.md P06「文言は辞書ファイルに集約し、画面に直書きしない」。
 * 文言規則（第6章）：ボタンは動詞で終える／確認は件数と対象を含める／破壊的操作は取り消せない旨を明記。
 */
export const messages = {
    app: {
        name: '営業支援ツール',
        tagline: '見込み企業の収集から、アポイント管理までを一元化します',
    },
    nav: {
        home: 'ホーム',
        collections: 'リスト収集',
        companies: '企業',
        lists: 'リスト',
        campaigns: 'キャンペーン',
        /** スマホ下部タブの短縮表記（折り返しを避ける） */
        campaignsShort: '施策',
        inbox: '受信',
        mail: 'メール',
        newsletter: 'メルマガ',
        appointments: 'アポイント',
        activities: '次回アクション',
        analytics: '分析',
        settings: '設定',
        logs: 'ログ',
        usage: 'コスト',
        jobs: '実行状況',
        more: 'その他',
        collapse: 'サイドバーを折りたたむ',
        expand: 'サイドバーを開く',
    },
    auth: {
        loginTitle: 'ログイン',
        email: 'メールアドレス',
        password: 'パスワード',
        submit: 'ログインする',
        forgot: 'パスワードをお忘れですか',
        invitedOnly: 'アカウントは管理者が発行します',
        // どちらが誤りかは示さない（docs/10-screen-spec.md S01）
        failed: 'メールアドレスまたはパスワードが正しくありません',
        locked: '試行回数の上限に達しました。15 分後に再度お試しください',
        logout: 'ログアウトする',
        passwordRule: '12 文字以上で、英大文字・英小文字・数字・記号のうち 3 種類以上を含めてください',
        passwordConfirm: 'パスワード（確認）',
        passwordMismatch: 'パスワードが一致しません',
        resetRequestTitle: 'パスワードの再設定',
        resetRequestSubmit: '再設定メールを送信する',
        // 存在しないアドレスでも同じ応答を返す（docs/11-security.md 2.1）
        resetRequestDone: '入力されたメールアドレス宛に再設定の手順を送信しました。メールをご確認ください',
        resetConfirmTitle: '新しいパスワードを設定',
        resetConfirmSubmit: 'パスワードを設定する',
        inviteTitle: 'アカウントの設定',
        inviteSubmit: '設定してはじめる',
        inviteExpired: '招待の有効期限が切れています。管理者に再送を依頼してください',
        displayName: '氏名',
    },
    common: {
        save: '保存する',
        saved: '保存しました',
        cancel: 'キャンセル',
        close: '閉じる',
        back: '戻る',
        next: '次へ',
        edit: '編集する',
        delete: '削除する',
        add: '追加する',
        apply: '適用する',
        reset: '条件をリセットする',
        search: '検索',
        filter: '絞り込み',
        filterCount: (n) => `絞込 ${n}`,
        selectAll: 'すべて選択',
        clearSelection: '選択を解除する',
        selectedCount: (n) => `${n.toLocaleString('ja-JP')} 件選択中`,
        loadMore: 'さらに読み込む',
        loading: '読み込んでいます',
        retry: '再試行する',
        copy: 'コピーする',
        copied: 'コピーしました',
        required: '必須',
        optional: '任意',
        yes: 'はい',
        no: 'いいえ',
        all: 'すべて',
        unset: '未設定',
        unassigned: '担当未設定',
        none: 'なし',
        unknown: '—',
        reason: '理由',
        detail: '詳細',
        source: '出典',
        createdAt: '作成日時',
        updatedAt: '更新日時',
        export: 'CSV 出力',
        import: 'CSV 取込',
        refresh: '最新の状態にする',
        // 破壊的操作の共通文言
        irreversible: 'この操作は取り消せません。',
        confirmTitle: '操作を確認してください',
        countItems: (n) => `${n.toLocaleString('ja-JP')} 件`,
        countCompanies: (n) => `${n.toLocaleString('ja-JP')} 社`,
    },
    errors: {
        validation: '入力内容を確認してください',
        forbidden: 'この操作を行う権限がありません',
        notFound: '対象が見つかりません',
        conflict: '対象の状態が変化しています。画面を更新してください',
        upstream: '外部サービスに接続できませんでした。時間をおいて再度お試しください',
        rateLimited: '短時間に操作が集中しました。しばらく待って再度お試しください',
        quotaExceeded: '利用上限に達しました。設定画面で上限を確認してください',
        internal: (traceId) => traceId ? `エラーが発生しました（ID: ${traceId}）` : 'エラーが発生しました',
        networkOffline: 'ネットワークに接続できません。接続を確認してください',
        searchApiUnavailable: '検索 API に接続できませんでした',
        mailNotConfigured: 'メール連携が未設定です。設定画面で接続してください',
        aiNotConfigured: 'AI の接続が未設定です。設定画面で接続してください',
    },
    empty: {
        companies: {
            title: 'まだ企業がありません',
            description: 'リスト収集から始めましょう。検索条件を設定すると企業を集められます',
            action: 'リスト収集を開く',
        },
        companiesFiltered: {
            title: '条件に一致する企業がありません',
            description: '絞り込み条件を見直してください',
            action: '条件をリセットする',
        },
        lists: {
            title: 'まだリストがありません',
            description: '企業一覧で企業を選び「リストに追加する」から作れます',
            action: '企業一覧へ',
        },
        campaigns: {
            title: 'まだキャンペーンがありません',
            description: '対象リストと商材を選ぶと、文面の生成から送信まで進められます',
            action: 'キャンペーンを作成する',
        },
        inbox: {
            title: '受信はありません',
            description: 'メール連携が有効な場合、5 分ごとに自動で取り込まれます',
            action: '受信を取得する',
        },
        appointments: {
            title: '予定されているアポイントはありません',
            description: '受信への対応からアポイントを登録できます',
            action: '受信を開く',
        },
        activities: {
            title: '次回アクションはありません',
            description: '企業詳細から期限つきのアクションを登録できます',
            action: '企業を開く',
        },
        products: {
            title: 'まだ商材がありません',
            description: '商材を登録すると、文面の生成と検索条件の初期値に使えます',
            action: '商材を登録する',
        },
        jobs: {
            title: '実行中の処理はありません',
            description: '収集や送信を開始すると、ここで進捗を確認できます',
            action: 'リスト収集を開く',
        },
        suggestions: {
            title: '提案はまだありません',
            description: '送信と返信のデータが集まると、提案を作成できます',
            action: '再生成する',
        },
    },
    collection: {
        title: 'リスト収集',
        start: '収集を開始する',
        stop: '収集を停止する',
        preview: '件数を試算する',
        previewNote: '試算でも検索 API の呼び出しが発生します',
        previewResult: (n) => `約 ${n.toLocaleString('ja-JP')} 件`,
        saveCondition: '条件を保存する',
        savedConditions: '保存済みの条件',
        recentRuns: '最近の収集',
        keywords: 'キーワード',
        keywordModeAnd: 'すべて含む',
        keywordModeOr: 'いずれか含む',
        excludeKeywords: '除外キーワード',
        industries: '業種',
        regions: '地域',
        limitCount: '取得件数上限',
        limitHint: (max) => `上限 ${max.toLocaleString('ja-JP')} 件（管理者設定）`,
        excludeDomains: '除外ドメイン',
        existingPolicy: '既存企業の扱い',
        existingSkip: '除外',
        existingRecrawl: '再収集して情報を更新',
        productHint: '商材を選ぶと、営業対象条件が業種・地域・キーワードに反映されます',
        productOverwriteConfirm: '現在の入力内容を商材の営業対象条件で置き換えますか。',
        confirmStart: (count, calls) => `最大 ${count.toLocaleString('ja-JP')} 件を収集します。検索 API の呼び出しは約 ${calls} 回です。`,
        progress: (done, total) => `${done.toLocaleString('ja-JP')} / ${total.toLocaleString('ja-JP')}`,
        resultSummary: (created, duplicate, excluded, error) => `新規${created} 重複${duplicate} 除外${excluded} エラー${error}`,
        zeroResult: '条件に一致する結果がありませんでした。キーワードを減らすか、地域を広げてください',
        limitReached: '取得件数の上限に達したため、収集を終了しました',
        tabAll: 'すべて',
        tabHasContact: '連絡先あり',
        tabNoContact: '連絡先なし',
        tabNeedsReview: '要確認',
    },
    company: {
        title: '企業',
        add: '企業を追加する',
        recrawl: '再取得する',
        changeStatus: '状況を変更する',
        changeAssignee: '担当を変更する',
        assign: '担当を割り当てる',
        exclude: '対象外にする',
        unexclude: '対象外を解除する',
        merge: '統合する',
        mergeUndo: '統合を取り消す',
        tabOverview: '概要',
        tabContacts: '連絡先',
        tabTimeline: '接触履歴',
        tabMessages: '文面',
        tabNotes: 'メモ',
        lastContacted: '最終接触',
        lastCrawled: '最終クロール',
        industryEstimated: '推定',
        aiSummary: 'AI 要約',
        call: '発信する',
        duplicateNotice: (n) => `重複の可能性がある企業が ${n} 件あります`,
        duplicateCheck: '確認する',
        mergeTitle: '企業を統合する',
        mergeKeep: '残す',
        mergeSource: '統合される側',
        mergeCarryOver: (sends, replies, notes) => `送信履歴 ${sends} 件・返信 ${replies} 件・メモ ${notes} 件 を引き継ぎます`,
        mergeUndoNote: 'この操作は当日中のみ取り消せます',
        excludeReasonRequired: '対象外にする理由を入力してください',
        statusChangeUndo: '状況の変更を取り消す',
        noContactWarning: '有効な連絡先がないため、送信対象に含まれません',
        securityScan: 'セキュリティチェック',
        securityRescan: 'セキュリティチェックを再実行する',
        tabSecurity: 'セキュリティ',
        securityNotRun: 'まだセキュリティチェックを実行していません',
        securityScanNote: '外部サイトの情報を通常のブラウザと同じ方法で 1 回だけ取得し、その結果を AI が評価します。攻撃的な診断は行いません',
        securityScanStarted: 'セキュリティチェックを開始しました',
        securityBulkStarted: (n) => `${n} 社のセキュリティチェックを開始しました`,
        securityFindings: '所見',
        securitySalesHook: '営業での切り口',
        securityRecommendation: '推奨対応',
        securitySignals: '収集した信号',
    },
    list: {
        title: 'リスト',
        create: 'リストを作成する',
        addToList: 'リストに追加する',
        createForCampaign: 'キャンペーン用リストを作る',
        createdForCampaign: (name, n) => `「${name}」を作成しました（${n} 社）`,
        fromSelection: '選択した企業から作る',
        fromSelectionNote: '選んだ企業をそのまま固定します。あとから絞り込みを変えても対象は変わりません',
        fromFilter: 'いまの絞り込み条件から作る',
        fromFilterNote: '条件リストとして保存します。対象はキャンペーン開始時点で評価されます',
        goToCampaign: '続けてキャンペーンを作る',
        removeFromList: 'リストから削除する',
        typeStatic: '静的リスト',
        typeDynamic: '条件リスト',
        dynamicNote: '条件リストの対象は、キャンペーン開始時点の評価結果で固定されます',
        inUse: 'キャンペーンで使用中のため削除できません',
        edit: 'リストを編集する',
        delete: 'リストを削除する',
        confirmDelete: (name) => `リスト「${name}」を削除します。所属する企業のデータは残ります。`,
        removeSelected: (n) => `${n.toLocaleString('ja-JP')} 社をリストから外す`,
        removedFromList: (n) => `${n.toLocaleString('ja-JP')} 社をリストから外しました`,
        addFromCompanies: '企業一覧から追加する',
        openInCompanies: 'この条件で企業一覧を開く',
        dynamicReadonly: '条件リストの対象は絞り込み条件で決まります。企業を個別に足したり外したりするには静的リストを使ってください',
        emptyStatic: 'まだ企業がありません。企業一覧で企業を選び「リストに追加する」から入れられます',
    },
    suppression: {
        title: '除外リスト',
        add: '除外を登録する',
        remove: '除外を解除する',
        removeConfirm: '解除すると今後の送信対象に含まれます。',
        // 返信検知による配信停止の解除は admin のみ（docs/10-screen-spec.md S73）
        removeAdminOnly: '返信から検知した配信停止の解除は管理者のみが行えます',
        importCsv: 'CSV で一括登録する',
    },
    message: {
        title: '文面',
        generate: '文面を生成する',
        bulkGenerate: 'まとめて生成する',
        regenerate: '再生成する',
        generateAlternative: '別案を生成する',
        approve: '承認する',
        bulkApprove: 'まとめて承認する',
        unapprove: '差し戻す',
        saveAsTemplate: 'テンプレートとして保存する',
        send: '送信する',
        testSend: 'テスト送信する',
        testSendNote: '署名・配信停止の案内・計測用リンクを含む、実際に送る形のメールを指定の宛先へ送ります。送信記録には残らず、件名の先頭に [テスト] が付きます。',
        testSendTo: 'テスト送信先',
        testSent: (to) => ` へテスト送信しました`,
        subject: '件名',
        body: '本文',
        rationale: '生成の根拠',
        rationaleProduct: '商材から',
        rationaleCompany: '企業情報から',
        assumptionsTitle: '推測を含みます',
        autosaved: (time) => `下書き保存済み ${time}`,
        charCount: (used, max) => `${used}/${max} 文字`,
        charOver: '文字数の上限を超えています。短くしてから送信してください',
        bodyOverNote: (max) => `推奨の ${max} 文字を超えています（送信はできます）`,
        model: '使用モデル',
        generatedAt: '生成日時',
        versionHistory: '版の履歴',
        restoreVersion: 'この版に戻す',
        discard: '破棄する',
        ngPhraseWarning: (phrase) => `禁止表現「${phrase}」が含まれています`,
        confirmSendTo: (target) => `${target} に送信します。`,
    },
    campaign: {
        title: 'キャンペーン',
        create: 'キャンペーンを作成する',
        start: '送信を開始する',
        pause: '一時停止する',
        resume: '再開する',
        cancel: '中止する',
        remove: 'キャンペーンを削除する',
        rename: '名前を変更する',
        renameNote: '名前は一覧や実績比較の表示に使います。送信中でも変更でき、送信には影響しません。',
        confirmRemove: (name) => `キャンペーン「${name}」を削除します。`,
        removeNote: '送信履歴・返信は残りますが、このキャンペーンとの紐付けは外れます。',
        removeBlocked: '実行中・予約中・一時停止中は削除できません。先に中止してください',
        emergencyStop: '全キャンペーンを停止する',
        sendNow: '今すぐ配信する',
        sendNowNote: '送信可能時間帯と 1 日あたり上限を無視して、送信できる対象に今から順に送ります。承認が必要な文面は承認済みのものだけ送ります。',
        sendNowRateNote: '組織の送信レート（1 時間あたりの件数、設定 → 利用上限）と同一ドメインの間隔は引き続き適用されます。',
        sendNowActive: '即時配信中（時間帯・1 日上限を無視）',
        sendNowStarted: (count) => `即時配信を開始しました（対象 ${count} 社）`,
        immediateOption: '今すぐ全件送る（送信可能時間帯と 1 日あたり上限を無視）',
        openSettings: '実行設定を開いて開始する',
        draftTargetsTitle: '開始前のため、対象はまだ確定していません',
        draftTargetsNote: '開始すると、この時点の対象が確定して送り始めます。内訳は送信前チェックの結果です。',
        stepBasic: '基本設定',
        stepTargets: '対象確認',
        stepMessages: '文面',
        stepSchedule: '実行設定',
        channelPolicy: 'チャネル',
        approvalPolicy: '承認要否',
        dailyLimit: '1日あたり上限',
        totalLimit: '全体上限',
        domainInterval: '同一ドメイン間隔',
        allowedHours: '送信可能時間帯',
        followup: '追客設定',
        scheduledAt: '開始日時',
        sendable: '送信可能',
        blocked: '除外',
        notApproved: '未承認',
        estimatedDays: (n) => `完了まで約 ${n} 日`,
        estimatedCost: (usd) => `想定費用 約 $${usd.toFixed(1)}`,
        // 確認ダイアログは件数と対象を必ず含める（docs/09-design-system.md 第6章）
        confirmStart: (count) => `${count.toLocaleString('ja-JP')} 社に送信を開始します。`,
        confirmStartNote: '開始後も一時停止できます。',
        confirmPause: '実行中の 1 件を終えてから停止します。',
        approvalNoneWarning: '生成された文面を確認せずに送信します。最初の 10 件の結果を確認することを推奨します。',
        preflightChanged: '対象の条件が変わりました。内容を確認してから再度開始してください',
        dailyLimitReached: (limit, resumeAt) => `本日の上限 ${limit} 件に達しました。${resumeAt} に再開します`,
        tabTargets: '対象',
        tabUnknown: '結果不明',
        tabNeedsAction: '要対応',
        tabMessages: '文面',
        tabTracking: '効果測定',
        tabSettings: '設定',
        generationMode: '文面の作り方',
        templateRequired: 'テンプレート差し込みを選ぶ場合はテンプレートを選んでください',
        preview: '下書きを全件確認する',
        previewTitle: '送信前プレビュー',
        previewNote: '実際に送られる本文です。署名・配信停止の案内・計測用リンクを含みます。',
        previewEmpty: '文面がまだありません。先に文面を生成してください',
        markSent: '送信済みとして記録する',
        markNotSent: '送信されていないと記録する',
        retry: '再送する',
        retryDuplicateWarning: 'すでに送信されている可能性があります。重複して届く場合があります。',
        needsActionHelp: '手順を見る',
        copyFormUrl: 'フォーム URL をコピーする',
        copyMessage: '文面をコピーする',
        regenerateAll: '文面を作り直す',
        confirmRegenerateAll: (n) => `${n.toLocaleString('ja-JP')} 社分の文面を作り直します。生成済みの下書きは新しい版に置き換わり、承認は取り消されます。`,
    },
    inbox: {
        title: '受信',
        sync: '受信を取得する',
        tabOpen: '未対応',
        tabNeedsLink: '要確認',
        tabAll: 'すべて',
        reply: '返信する',
        replyDraft: 'AI で下書き',
        createAppointment: 'アポを登録する',
        markDone: '対応完了にする',
        changeClass: '分類を変更する',
        linkCompany: '企業を選ぶ',
        linkedTo: '紐付け先',
        unlinked: '紐付け先が未確定',
        matchCandidates: (n) => `候補 ${n} 件`,
        applyToSameDomain: '同じドメインからの未紐付けの受信も同じ企業に紐付ける',
        classEvidence: '判定根拠',
        showQuote: '引用部分を表示する',
        hideQuote: '引用部分を隠す',
        showImages: '画像を表示する',
        imagesBlocked: '外部の画像は読み込んでいません',
        unsubscribedNotice: 'この企業は配信停止に登録されました。今後の自動送信は行われません',
        sentMessageLink: '送信した文面を見る',
        nextActionPrompt: '次回アクションを登録しますか',
    },
    appointment: {
        title: 'アポイント',
        create: 'アポイントを登録する',
        update: 'アポイントを変更する',
        recordResult: '結果を登録する',
        scheduledAt: '日時',
        duration: '所要時間',
        format: '形式',
        location: '場所',
        locationUrl: '会議 URL',
        owner: '担当',
        participants: '同席者',
        purpose: '目的',
        note: 'メモ',
        resultNote: '実施メモ',
        nextAction: '次回アクション',
        nextActionDue: '期限',
        today: '今日',
        thisWeek: '今週',
        confirmStatusUpdate: '企業の営業状況を「アポ確定」に更新します。',
    },
    activity: {
        title: '次回アクション',
        create: '次回アクションを登録する',
        complete: '完了にする',
        changeDue: '期限を変更する',
        overdue: '期限超過',
        overdueCount: (n) => `期限超過 ${n} 件`,
        dueToday: '本日期限',
        mineOnly: '自分の担当のみ',
        showAll: 'すべて表示',
    },
    analytics: {
        title: '分析',
        tabSummary: 'サマリ',
        tabTimeseries: '推移',
        tabBreakdown: '比較',
        tabFunnel: 'ファネル',
        tabSuggestions: 'AI提案',
        sendPeriod: '送信期間',
        observationPeriod: '観測期間',
        observationDaysLabel: (n) => `送信後${n}日`,
        measuring: (n) => `計測中 ${n.toLocaleString('ja-JP')} 件`,
        measuringNote: (n) => `観測期間が満了していない企業 ${n.toLocaleString('ja-JP')} 件を「計測中」として表示しています`,
        collected: '収集',
        sent: '送信',
        replied: '返信',
        appointment: 'アポ',
        negotiating: '商談',
        won: '受注',
        replyRate: '返信率',
        appointmentRate: 'アポイント率',
        excludedTitle: '集計対象外',
        comparedToPrevious: '前期間比',
        // 件数が閾値未満の集計は「参考値」と明示する（docs/15-glossary.md G06）
        reference: '参考値',
        referenceNote: (threshold) => `対象が ${threshold} 件未満のため参考値として表示しています`,
        uniqueNote: '企業単位でユニーク化しているため、合計と一致しません',
        // 個人の比較は数値の提示にとどめる（G07）
        memberScopeNote: '自分の実績と全体平均のみ表示しています',
        medianDays: (n) => `中央値 ${n} 日`,
    },
    suggestion: {
        title: 'AI からの提案',
        refresh: '提案を再生成する',
        adopt: '採用する',
        reject: '却下する',
        rejectReason: '却下の理由（任意）',
        rationale: '根拠',
        dataPeriod: '参照期間',
        sampleSize: (n) => `対象 ${n.toLocaleString('ja-JP')} 件`,
        hypothesis: '仮説',
        hypothesisNote: '推測を含みます。事実として扱わないでください',
    },
    settings: {
        title: '設定',
        products: '商材',
        users: 'ユーザー',
        suppressions: '除外リスト',
        tags: 'タグ',
        newsletter: 'メルマガ',
        integrations: '外部連携',
        limits: '利用上限',
        organization: '組織',
        noPermission: 'この設定を変更する権限がありません',
        testConnection: '接続をテストする',
        testSucceeded: '接続を確認しました',
        testFailed: '接続できませんでした',
        lastTestedAt: '最終テスト',
        keyRegistered: '登録済み',
        keyNotRegistered: '未登録',
        // APIキーは登録後に平文を返さない（docs/05-api-spec.md 第3章）
        keyMasked: '登録済みのキーは表示されません。更新する場合は新しい値を入力してください',
        disconnect: '連携を解除する',
        inviteUser: 'ユーザーを招待する',
        resendInvite: '招待を再送する',
        suspendUser: '停止する',
        activateUser: '再開する',
        deleteUser: 'ユーザーを削除する',
        handoverRequired: '担当している企業があります。引き継ぎ先を選んでください',
        ngPhrases: '禁止表現',
        strengths: '強み',
        painPoints: '想定課題',
        targetCriteria: '営業対象条件',
        cases: '導入事例',
        casePublic: '文面で使用してよい',
        pricingPublic: '文面で価格に触れてよい',
        archiveProduct: '商材を停止する',
        productInUse: 'キャンペーンで使用中のため削除できません。停止扱いにしてください',
        templates: '文面テンプレート',
        mailSend: 'メール送信',
        mailDriver: '送信方法',
        fromAddress: '差出人アドレス',
        fromName: '差出人名',
        replyTo: '返信先アドレス',
        resendApiKey: 'Resend API キー',
        resendNote: 'Resend の管理画面で送信元ドメインを検証し、そのドメインのアドレスを差出人に設定してください。',
        smtpHost: 'SMTP ホスト',
        smtpPort: 'ポート',
        smtpUser: 'ユーザー',
        smtpPassword: 'パスワード',
        smtpSecure: 'SSL/TLS で接続する',
        tracking: '効果測定',
        signature: '署名・差し込み項目',
        signatureNote: '文面の末尾に付ける署名と、テンプレートの {{sender_company}} 等に入る値です',
        senderCompany: '自社名',
        senderPerson: '担当者名',
        senderContact: '連絡先（メールアドレスまたは電話番号）',
        unsubscribeNotice: '配信停止の案内',
        schedulingUrl: '日程調整 URL',
        schedulingUrlNote: 'テンプレートの {{scheduling_url}} に入ります',
        newsletterUrl: 'メルマガ登録 URL',
        newsletterUrlNote: 'テンプレートの {{newsletter_url}} に入ります',
        aiSettings: 'AI の利用設定',
        aiSettingsNote: '文面生成・返信分類・セキュリティチェックで使うプロバイダとモデルです。API キーは下の各連携に登録します。',
        aiProvider: 'プロバイダ',
        aiModel: 'モデル',
        aiModelNote: '候補から選ぶか、プロバイダが公開しているモデル ID を直接入力できます',
        aiEffort: '推論の深さ',
        aiEffortNote: '高いほど精度が上がり、時間と費用がかかります。Claude の effort・OpenAI の reasoning_effort・Gemini の thinking level に対応します',
        aiKeyMissing: (label) => `${label} の API キーが登録されていません`,
        timerexNote: 'TimeRex のチーム設定 → デベロッパーツールで作った API キー（64 桁）を登録し、予約を受けるカレンダーを選びます。文面の {{scheduling_url}} には企業ごとのワンタイム URL が入り、予約が入るとアポイントが自動で登録されます。',
        timerexCalendar: '予約を受けるカレンダー',
        timerexCalendarNote: '限定フォルダ内のカレンダーは選べません',
        timerexNoCalendars: 'カレンダーが見つかりません。TimeRex 側でカレンダーを作成してください',
        timerexLoadCalendars: 'カレンダーを読み込む',
        trackOpens: '開封を計測する',
        trackClicks: 'リンクのクリックを計測する',
        htmlMail: 'HTML でも送る',
        htmlMailNote: '開封・クリックの計測には HTML 本文が必要です。',
        unsubscribeLink: '配信停止リンクを本文に入れる',
        unsubscribeLinkNote: '押した相手は自動で除外リストに登録され、以降の送信対象から外れます。',
        mailReceive: 'メール受信',
        googleWorkspace: 'Google Workspace',
        mailReceiveNote: '相手からの返信を受信箱から取り込み、企業に紐付けて分類します。取り込みは 5 分ごとに自動で行われます。',
        receiveAuth: '認証方式',
        receiveUser: '受信箱のアドレス',
        receiveUserNote: '返信先にしているグループのメンバーに加えた受信用ユーザーを指定します。このユーザーの受信箱を読みます',
        serviceAccountKey: 'サービスアカウントの鍵（JSON）',
        serviceAccountKeyNote: 'Google Cloud で発行した鍵ファイルの中身をそのまま貼り付けます。登録後は表示されません',
        serviceAccountRegistered: (email) => `登録済み：${email}`,
        imapHost: 'IMAP ホスト',
        imapPort: 'ポート',
        imapPassword: 'パスワード',
        mailbox: 'メールボックス',
        mailboxNote: '通常は INBOX のままで構いません',
        receiveSince: '取り込み開始日',
        receiveSinceNote: '空欄なら、設定した時点より後に届くメールだけを取り込みます。過去の返信も取り込む場合は日付を指定します',
        fetchNow: '今すぐ取り込む',
        fetchStarted: '受信の取り込みを開始しました。結果は受信画面で確認できます',
        googleWorkspaceSteps: [
            '受信用のユーザー（例：sales-inbox@自社ドメイン）を作り、返信先にしているグループのメンバーに加える',
            'Google Cloud でサービスアカウントを作り、JSON の鍵を発行する',
            '管理コンソール → セキュリティ → アクセスとデータ管理 → API の制御 → ドメイン全体の委任 で、サービスアカウントのクライアント ID にスコープ https://mail.google.com/ を許可する',
            '下に受信箱のアドレスと鍵を登録し、接続をテストする',
        ],
    },
    template: {
        title: '文面テンプレート',
        create: 'テンプレートを作成する',
        edit: 'テンプレートを編集する',
        name: 'テンプレート名',
        channel: 'チャネル',
        subject: '件名',
        body: '本文',
        insertVariable: '差し込み項目',
        previewWithSample: '見本の値で確認する',
        inUse: 'キャンペーンで使用中のため削除できません',
        variableNote: '{{company_name}} のように書くと、送信時に企業ごとの値へ置き換わります。',
        unresolved: (keys) => `置き換えられない項目があります：${keys.join('、')}`,
    },
    tracking: {
        title: '効果測定',
        trackedSends: '計測対象の送信',
        delivered: '到達',
        opened: '開封',
        clicked: 'クリック',
        unsubscribed: '配信停止',
        bounced: '配信不能',
        complained: '迷惑報告',
        openRate: '開封率',
        clickRate: 'クリック率',
        clickThroughRate: '開封後クリック率',
        topLinks: 'クリックされたリンク',
        unsubscribeLink: '配信停止リンク',
        unsubscribeLinkNote: '配信停止ページを開いた企業・配信停止した企業（クリック率には含めません）',
        unsubscribedDone: '配信停止済み',
        noEvents: 'まだ計測結果がありません',
        disabled: '効果測定は設定で無効になっています',
    },
    quota: {
        aiCost: 'AI 費用',
        searchCalls: '検索呼び出し',
        dailySend: '1日の送信',
        warning: (pct) => `今月の利用が上限の ${pct}% に達しています`,
        blocked: '利用上限に達したため、この機能は停止しています',
        openSettings: '設定を開く',
        usedOf: (used, limit) => `${used} / ${limit}`,
    },
    jobs: {
        title: '実行状況',
        // 「ジョブ」「キュー」は設定・ログ画面以外では「処理」と言い換える
        runningCount: (n) => `実行中 ${n}`,
        cancel: '停止する',
        confirmCancel: '実行中の 1 件を終えてから停止します。',
        stalled: '30 分以上更新がない処理があります',
        tabRunning: '実行中',
        tabQueued: '待機中',
        tabFinished: '完了',
        startedAt: '開始',
        startedBy: '実行者',
        progressOf: (done, total) => `${done.toLocaleString('ja-JP')} / ${total.toLocaleString('ja-JP')}`,
    },
    logs: {
        title: 'ログ',
        tabAudit: '操作',
        tabCollection: '収集',
        tabCrawl: 'クロール',
        tabSend: '送信',
        tabAi: 'AI',
        tabError: 'エラー',
        maskedNotice: '秘密情報はマスクして記録しています',
        traceId: 'trace id',
        exportAudit: '操作ログを CSV 出力する',
    },
    usage: {
        title: 'コスト',
        provider: 'プロバイダ',
        feature: '機能',
        calls: '呼び出し',
        tokens: 'トークン',
        cost: '費用',
        blockedFeatures: '上限に達すると停止する機能',
    },
    notifications: {
        title: '通知',
        markRead: '既読にする',
        markAllRead: 'すべて既読にする',
        collectionFinished: (name, n) => `収集「${name}」が完了しました（${n} 社）`,
        campaignFinished: (name) => `キャンペーン「${name}」が完了しました`,
        campaignNeedsAction: (name, n) => `キャンペーン「${name}」に要対応が ${n} 件あります`,
        exportReady: 'CSV の生成が完了しました',
        quotaWarning: (label, pct) => `${label}の利用が上限の ${pct}% に達しました`,
        activityDue: (company) => `${company} の次回アクションの期限が近づいています`,
    },
    dashboard: {
        title: 'ホーム',
        needsAttention: '要対応',
        running: '実行中の処理',
        todayAppointments: '今日のアポイント',
        unknownSends: (n) => `結果不明の送信 ${n} 件`,
        needsActionSends: (n) => `要対応（CAPTCHA等） ${n} 件`,
        openReplies: (n) => `未対応の返信 ${n} 件`,
        overdueActivities: (n) => `期限超過の次回アクション ${n} 件`,
        check: '確認する',
        openInbox: '受信を開く',
        open: '開く',
        periodToday: '今日',
        periodWeek: '今週',
        periodMonth: '今月',
        periodLastMonth: '先月',
        periodCustom: '任意期間',
    },
};
//# sourceMappingURL=messages.js.map